import React from 'react';
import { CheckIcon, GiftIcon } from 'lucide-react';
import { Chip, Eyebrow, Section, SectionHeading } from './Shared';
import { Bagua, LeafSpray } from './Decor';
import { bonuses, tomos } from '../data/pack';
const accentBorder: Record<string, string> = {
  jade: 'border-l-brand-jade',
  wood: 'border-l-brand-wood',
  cinnabar: 'border-l-brand-cinnabar',
  water: 'border-l-brand-water',
  gold: 'border-l-brand-gold',
  silver: 'border-l-brand-silver'
};
const accentText: Record<string, string> = {
  jade: 'text-brand-jade',
  wood: 'text-brand-wood',
  cinnabar: 'text-brand-cinnabar',
  water: 'text-brand-water',
  gold: 'text-brand-gold',
  silver: 'text-brand-silver'
};
const accentBadge: Record<string, string> = {
  jade: 'bg-brand-jade',
  wood: 'bg-brand-wood',
  cinnabar: 'bg-brand-cinnabar',
  water: 'bg-brand-water',
  gold: 'bg-brand-gold',
  silver: 'bg-brand-silver'
};
export function PackContents() {
  return <Section id="contenido" glow data-id="element-256" data-mp="11kzmbw6mos9e">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10 overflow-hidden" data-id="element-257" data-mp="11l9arfc0yrcz">
        <Bagua className="absolute -left-20 top-52 hidden h-72 w-72 text-brand-jade/10 lg:block" data-id="element-258" data-mp="3k8bdgebzab29" />
        <LeafSpray className="absolute -right-10 bottom-24 hidden h-56 w-56 text-brand-jade/12 lg:block" data-id="element-259" data-mp="2rtfeut1oh3k" />
      </div>

      <SectionHeading eyebrow="Todo incluido" lead="Todo lo que necesitás para detectar, corregir, activar y mantener la armonía de tu negocio o consultorio." className="mb-12" data-id="element-260" data-mp="3u5xv38knwgpx">
        El Sistema Completo de <span className="title-seal" data-id="element-261" data-mp="6twuruiu45u4">Feng Shui</span> para tu Negocio
      </SectionHeading>

      <p className="mx-auto -mt-6 mb-12 text-center font-title text-sm uppercase tracking-[0.22em] text-brand-wood md:text-base" data-id="element-262" data-mp="lqe82ral3ujb">
        3 Tomos Principales + 6 Bonus Estratégicos
      </p>

      {/* Tomos: el corazón del sistema, con el mayor peso visual de la página. */}
      <div className="mx-auto max-w-6xl space-y-8" data-id="element-263" data-mp="2njvjm61u5fjc">
        {tomos.map(tomo => <article key={tomo.label} className={`paper-panel grid gap-6 border-l-4 p-5 md:grid-cols-[minmax(0,170px)_minmax(0,1fr)] md:gap-8 md:p-8 ${accentBorder[tomo.color]}`} data-id="element-264" data-mp="2ikirb5s73g9d">
            <div className="mx-auto w-32 sm:w-36 md:mx-0 md:w-full md:self-start" data-id="element-265" data-mp="3c9q7mojwfgr8">
              <img src={tomo.cover} alt={tomo.coverAlt} loading="lazy" draggable={false} className="block h-auto w-full border border-brand-ink/12 shadow-[0_10px_24px_rgba(36,28,20,0.18)]" data-id="element-266" data-mp="3h7i6k2iyo34x" />
            </div>

            <div className="min-w-0" data-id="element-267" data-mp="3r4wdnbizvfkk">
              <p className={`font-title text-xs uppercase tracking-[0.28em] ${accentText[tomo.color]}`} data-id="element-268" data-mp="3r3uyovyuy8xk">{tomo.label}</p>
              <h3 className="mt-2 font-title text-2xl leading-tight text-brand-jadeDeep md:text-4xl" data-id="element-269" data-mp="3svazqtp2sll">{tomo.title}</h3>
              <p className="mt-2 font-title text-base text-brand-wood md:text-xl" data-id="element-270" data-mp="8shntz5m26tv">{tomo.subtitle}</p>
              <p className="mt-3 font-serif text-sm leading-relaxed text-brand-inkLight md:text-base" data-id="element-271" data-mp="drktnltf0h2w">{tomo.promise}</p>

              <div className="hair-rule my-6" aria-hidden="true" data-id="element-272" data-mp="ir763t1f5tan" />

              <ul className="grid gap-2.5 text-sm leading-snug text-brand-ink sm:grid-cols-2 md:text-[15px]" data-id="element-273" data-mp="237wze9bjookt">
                {tomo.features.map(feature => <li key={feature} className="flex gap-2.5" data-id="element-274" data-mp="1y8afxi54ic3k">
                    <CheckIcon className={`mt-0.5 h-4 w-4 shrink-0 ${accentText[tomo.color]}`} aria-hidden="true" data-id="element-275" data-mp="1t9h37rxnup4g" />
                    <span data-id="element-276" data-mp="1obz56wl6lub8">{feature}</span>
                  </li>)}
              </ul>

              <p className="mt-6 border-l-2 border-brand-gold bg-brand-rice px-4 py-3 font-serif text-sm italic leading-relaxed text-brand-wood md:text-base" data-id="element-277" data-mp="2lz7ja6ius137">
                {tomo.closing}
              </p>
            </div>
          </article>)}
      </div>

      {/* Bonus */}
      <div className="mx-auto mb-10 mt-20 max-w-3xl text-center" data-id="element-278" data-mp="279rwt2qcuhtb">
        <Eyebrow className="flex items-center justify-center gap-2" data-id="element-279" data-mp="2798n29auyzqd">
          <GiftIcon className="h-4 w-4" aria-hidden="true" data-id="element-280" data-mp="34q7g1twlaf71" />
          Siempre incluidos
        </Eyebrow>
        <h3 className="mt-3 font-title text-2xl leading-tight text-brand-jadeDeep md:text-4xl" data-id="element-281" data-mp="2zxe9d0hk8wsb">
          6 Bonus Estratégicos — <span className="text-brand-cinnabar" data-id="element-282" data-mp="2keux1u9z7uh4">siempre incluidos</span>
        </h3>
        <div className="double-rule mx-auto mt-5 w-24" aria-hidden="true" data-id="element-283" data-mp="2pgagxj0f3uxx" />
        <p className="mt-5 font-serif text-base leading-relaxed text-brand-inkLight md:text-lg" data-id="element-284" data-mp="2aj1kwc0662zv">
          Complementos visuales creados para que puedas aplicar el sistema de manera más rápida.
        </p>
      </div>

      <div className="mx-auto grid max-w-6xl gap-5 sm:grid-cols-2 lg:grid-cols-3" data-id="element-285" data-mp="337yu7pmekpgh">
        {bonuses.map(bonus => <article key={bonus.number} className="paper-panel flex h-full flex-col overflow-hidden" data-id="element-286" data-mp="384p7y6aqj8yq">
            <div className="flex flex-1 flex-col p-5 md:p-6" data-id="element-287" data-mp="2tgcg9liokca1">
              <div className="flex items-start gap-4" data-id="element-288" data-mp="2yfp4a9lafpe7">
                <div className="w-[84px] shrink-0 sm:w-[92px]" data-id="element-289" data-mp="2yedyy5c729kz">
                  <img src={bonus.cover} alt={bonus.coverAlt} loading="lazy" draggable={false} className="block h-auto w-full border border-brand-ink/12 shadow-[0_8px_18px_rgba(36,28,20,0.16)]" data-id="element-290" data-mp="3m4qhea52fopt" />
                </div>
                <div className="min-w-0" data-id="element-291" data-mp="1ffy8l92wf4g8">
                  <span className={`inline-block rounded-sm px-2 py-0.5 font-title text-[9px] uppercase tracking-[0.18em] text-white ${accentBadge[bonus.color]}`} data-id="element-292" data-mp="1aifxpd5bmf5l">
                    Bonus {bonus.number}
                  </span>
                  <h4 className="mt-2 font-title text-base leading-tight text-brand-jadeDeep md:text-lg" data-id="element-293" data-mp="1k7zkuv02gv69">
                    {bonus.title}
                  </h4>
                  <p className="mt-1.5 text-xs font-semibold uppercase tracking-wide text-brand-muted" data-id="element-294" data-mp="rjk5g7d0waue">
                    {bonus.tagline}
                  </p>
                </div>
              </div>

              <p className="mt-4 font-serif text-sm leading-relaxed text-brand-inkLight" data-id="element-295" data-mp="11hrk2jjuagt8">{bonus.description}</p>

              {bonus.lines && <ul className="mt-3 space-y-1 text-sm font-medium text-brand-ink" data-id="element-296" data-mp="2gxqn5gelnmmm">
                  {bonus.lines.map(line => <li key={line} data-id="element-297" data-mp="2cvve1pqfg4ya">{line}</li>)}
                </ul>}

              {bonus.chips && <ul className="mt-4 flex flex-wrap gap-1.5" data-id="element-298" data-mp="19mixphaubo0x">
                  {bonus.chips.map(chip => <li key={chip} data-id="element-299" data-mp="1eku57ldnxmfz">
                      <Chip data-id="element-300" data-mp="1ejsr181coju1">{chip}</Chip>
                    </li>)}
                </ul>}

              {bonus.closing && <p className="mt-auto pt-5 font-serif text-sm italic leading-relaxed text-brand-wood" data-id="element-301" data-mp="1jli22nokoxi4">{bonus.closing}</p>}
            </div>
          </article>)}
      </div>

      <p className="mx-auto mt-12 max-w-xl border-y-2 border-brand-gold bg-brand-paper px-6 py-4 text-center font-title text-sm uppercase tracking-[0.14em] text-brand-jadeDeep md:text-base" data-id="element-302" data-mp="qw1dk142q0r6">
        Todo el valor de los 6 Bonus <span className="text-brand-cinnabar" data-id="element-303" data-mp="zvefm8pvbn1v">incluido en tu pack</span>
      </p>
    </Section>;
}